import { ErrorsContext } from "@/react/story/providers/ErrorsContext";
import { useItems } from "@/react/story/providers/ItemsContext";
import { useLocales } from "@/react/story/providers/LocalesContext";
import { type Block, type Content, ContentTypeEnum, type Locale, type StoryPage } from "@/react/story/types";
import TranslationUtils from "@/react/story/utils/TranslationUtils";
import { type ReactNode, useMemo } from "react";

type Props = {
    children: ReactNode;
}

export default function ErrorsProvider(props: Props) {
    const Locales = useLocales();
    const Items = useItems();

    const inContent = (content: Content, locale: Locale | null = null) => {
        const errors = [];

        locale ??= Locales.current;

        if (TranslationUtils.isTranslatable(content) && !TranslationUtils.has(content.config.text, locale)) {
            errors.push("Il manque une traduction");
        }
        if (ContentTypeEnum.IMAGE === content.type && !content.config.image?.uri) {
            errors.push("Il manque un asset");
        }
        if (ContentTypeEnum.VIDEO === content.type && !(content.config.src || content.config.video?.uri)) {
            errors.push("Il manque une vidéo");
        }
        if (ContentTypeEnum.AUDIO === content.type && !content.config.src) {
            errors.push("Il manque un asset");
        }

        return errors;
    };

    const inBlock = (block: Block, locale: Locale | null = null) => {
        let errors: string[] = [];
        block.contents.forEach(content => {
            errors = [
                ...errors,
                ...inContent(content, locale),
            ];
        });

        return errors;
    };

    const inPage = (page: StoryPage, locale: Locale | null = null) => {
        return [
            ...inBlock(page.top, locale),
            ...(page.bottom ? inBlock(page.bottom, locale) : []),
        ];
    };

    const inLocale = (locale: Locale) => {
        let errors: string[] = [];
        Items.routes.forEach(r => {
            errors = [
                ...errors,
                ...inPage(r.page, locale),
            ];
        });

        return errors;
    };

    const context = useMemo(() => ({
        inLocale,
        inPage,
        inBlock,
        inContent,
    }), [ Locales.current, Items.routes ]);

    return (
        <ErrorsContext.Provider value={context}>
            {props.children}
        </ErrorsContext.Provider>
    );
}
